import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { CheckCircle, Shield, Leaf, Award } from "lucide-react";

const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const features = [
    {
      icon: Leaf,
      title: "Matières écologiques",
      description: "Matières certifiées FSC issues de forêts gérées durablement",
    },
    {
      icon: Shield,
      title: "Normes européennes",
      description: "Conformité totale aux normes d'emballage alimentaire UE",
    },
    {
      icon: Award,
      title: "ISO 9001:2015",
      description: "Système de management qualité certifié",
    },
    {
      icon: CheckCircle,
      title: "Contrôle qualité",
      description: "Contrôles avant, pendant et après production",
    },
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-accent/5 via-background to-primary/5 relative overflow-hidden" ref={ref}>
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary/10 to-transparent" />
      <div className="absolute bottom-0 left-0 w-1/3 h-1/2 bg-gradient-to-tr from-eco/10 to-transparent" />
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="inline-block text-sm font-bold text-primary uppercase tracking-widest mb-4 bg-primary/10 px-4 py-2 rounded-full"
          >
            À propos de nous
          </motion.span>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-accent mb-4"
          >
            Qui sommes-nous ?
          </motion.h2>
        </div>

        {/* Text + Features Grid */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
          {/* Text Content */}
          <div>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg text-muted-foreground leading-relaxed mb-6"
            >
              <strong className="text-foreground text-xl">AMS DISTRIB</strong>, fournisseur d'emballages alimentaires 
              basé à Bordeaux, propose des emballages durables certifiés FSC à destination des 
              restaurants et traiteurs en Gironde, Toulouse, Paris et partout en France. 
              Notre engagement : qualité, sécurité alimentaire et respect de l'environnement.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-muted-foreground leading-relaxed mb-8"
            >
              Notre processus de contrôle qualité rigoureux est effectué par un organisme 
              certificateur indépendant avant, pendant et après chaque production, 
              garantissant une conformité totale aux normes européennes d'emballage alimentaire.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-wrap gap-3"
            >
              <span className="px-5 py-2.5 bg-eco/15 text-eco rounded-full text-sm font-bold border border-eco/30 shadow-sm">
                Certifié FSC®
              </span>
              <span className="px-5 py-2.5 bg-primary/15 text-primary rounded-full text-sm font-bold border border-primary/30 shadow-sm">
                ISO 9001:2015
              </span>
              <span className="px-5 py-2.5 bg-accent/15 text-accent rounded-full text-sm font-bold border border-accent/30 shadow-sm">
                Contact alimentaire
              </span>
            </motion.div>
          </div>

          {/* Features Grid */}
          <div className="grid sm:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                className="group p-6 rounded-2xl bg-white/80 backdrop-blur-sm border border-primary/10 shadow-lg hover:shadow-xl hover:border-primary/30 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/20 to-eco/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <feature.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-display text-lg font-bold text-foreground mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Video Section - After Text */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="relative max-w-5xl mx-auto rounded-3xl overflow-hidden shadow-2xl ring-4 ring-primary/20"
          >
            <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 via-transparent to-eco/20 z-10 pointer-events-none" />
            <video
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-auto aspect-video object-cover"
              src="/videos/about-ams.mov"
            >
              Votre navigateur ne supporte pas la lecture de vidéos.
            </video>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;